import React, { useState, useEffect } from 'react';
import { CompositeCard as CompositeCardComponent } from './CompositeCard';
import { chatCardAgent } from '../../services/ChatCardAgent';
import { 
  CompositeCard as CompositeCardType, 
  CardType, 
  CardData, 
  CardContext, 
  CardLocation,
  ContentCardType,
  ContextCardType
} from '../../types/compositeCards';
import { ChatMessage } from '../../contexts/ChatContext';
import { ProcessFlowCard } from '../Demo/ProcessFlowCard';

interface CompositeCardRendererProps {
  message: ChatMessage;
  context: CardContext;
  onCardSelect?: (card: CompositeCardType) => void;
  onCardAction?: (action: string, payload?: any) => void;
}

export const CompositeCardRenderer: React.FC<CompositeCardRendererProps> = ({
  message,
  context,
  onCardSelect,
  onCardAction
}) => {
  const [card, setCard] = useState<CompositeCardType | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const renderCardFromMessage = async () => {
      // Only log in development mode and sample to reduce verbosity
      if (process.env.NODE_ENV === 'development' && Math.random() < 0.1) {
        console.log('🔍 [CompositeCardRenderer] Starting render (sampled):', {
          messageId: message.id,
          cardType: message.metadata?.cardType,
          hasMetadata: !!message.metadata
        });
      }

      if (!message.metadata) {
        console.warn('⚠️ [CompositeCardRenderer] No metadata found');
        setLoading(false);
        return;
      }

      // Infer cardType for legacy messages that don't have it
      let inferredCardType = message.metadata.cardType;
      if (!inferredCardType) {
        // Try to infer from message content or metadata structure
        if (message.metadata.artifactId || (message.metadata as any).artifact) {
          inferredCardType = 'artifact';
        } else if (message.metadata.taskId || (message.metadata as any).task) {
          inferredCardType = 'task';
        } else if ((message.metadata as any).processId || (message.metadata as any).process) {
          inferredCardType = 'process';
        } else if (message.type === 'card') {
          // If message type is 'card' but no cardType, default to artifact
          inferredCardType = 'artifact';
        }
        
        if (inferredCardType) {
          // Update metadata with inferred cardType for future use
          message.metadata.cardType = inferredCardType;
          console.log('✅ [CompositeCardRenderer] Inferred cardType for legacy message:', {
            messageId: message.id,
            inferredCardType
          });
        } else {
          console.warn('⚠️ [CompositeCardRenderer] Skipping message without cardType:', message.id);
          setLoading(false);
          return null;
        }
      }

      // Provide default context values if incomplete (instead of skipping)
      const defaultContext: CardContext = {
        threadId: context?.threadId || 'default',
        userId: context?.userId || 'current-user',
        uiRole: context?.uiRole || 'architect',
        modelType: context?.modelType || 'Company',
        dataSource: context?.dataSource || 'live'
      };
      
      // Use provided context or defaults
      const cardContext = context && context.threadId && context.userId && context.uiRole && context.modelType
        ? context
        : defaultContext;
      
      if (!context || !context.threadId || !context.userId || !context.uiRole || !context.modelType) {
        console.warn('⚠️ [CompositeCardRenderer] Using default context values for incomplete context:', {
          messageId: message.id,
          hasContext: !!context,
          hasThreadId: !!context?.threadId,
          hasUserId: !!context?.userId,
          hasUIRole: !!context?.uiRole,
          hasModelType: !!context?.modelType,
          usingDefaults: true
        });
      }

      // Only log in development mode and sample to reduce verbosity
      if (process.env.NODE_ENV === 'development' && Math.random() < 0.1) {
        console.log('🔍 [CompositeCardRenderer] Context validated, processing cardType (sampled):', inferredCardType);
      }

      try {
        const { cardType: rawCardType = inferredCardType, artifactId, taskId } = message.metadata;
        // Normalize cardType: trim whitespace and handle case variations
        const cardType = typeof rawCardType === 'string' ? rawCardType.trim() : rawCardType;
        let cardData: CardData;
        let cardTypeEnum: CardType;

        // Map legacy card types to new composite card types
        // Handle Container (case-insensitive) and other variations
        const normalizedCardType = typeof cardType === 'string' ? cardType.toLowerCase() : cardType;
        switch (cardType) {
          case 'artifact-overview':
          case 'artifact-detail':
          case 'artifact':
          case 'Container':
          case 'container': {  // Handle lowercase container - wrap in braces for lexical declarations
            // Also check normalized version as fallback
            // Only log in development mode
            if (normalizedCardType === 'container' && process.env.NODE_ENV === 'development') {
              console.log('🔍 [CompositeCardRenderer] Detected Container card type (normalized)');
            }
            // Only log detailed processing in development mode and sample to reduce verbosity
            if (process.env.NODE_ENV === 'development' && Math.random() < 0.1) {
              console.log('🔍 [CompositeCardRenderer] Processing artifact card (sampled):', {
                hasArtifact: !!(message.metadata as any).artifact,
                artifactId,
                metadataKeysCount: Object.keys(message.metadata).length,
                cardType: cardType
              });
            }
            
            cardTypeEnum = 'artifact' as ContentCardType;
            // Extract artifact data from message metadata or use provided data
            // Check multiple possible locations for artifact data
            const artifact = (message.metadata as any).artifact || 
                           (message.metadata as any).data?.artifact ||
                           (message as any).artifact;
            
            if (artifact) {
              const actualArtifactId = artifact.id || artifactId;
              
              // Only log in development mode and sample to reduce verbosity
              if (process.env.NODE_ENV === 'development' && Math.random() < 0.1) {
                console.log('🔍 [CompositeCardRenderer] Using artifact from metadata (sampled):', {
                  hasActionConfig: !!(message.metadata as any).actionConfig,
                  artifactId: actualArtifactId,
                  artifactName: artifact.name?.substring(0, 50)
                });
              }
              cardData = {
                artifact: artifact,
                selectedArtifactId: actualArtifactId,
                allArtifacts: (message.metadata as any).allArtifacts || [],
                actionConfig: (message.metadata as any).actionConfig
              };
            } else {
              // Fallback - try to create artifact from message text if available
              const artifactName = message.text || 'Unknown Artifact';
              // Only log in development mode and sample to reduce verbosity
              if (process.env.NODE_ENV === 'development' && Math.random() < 0.05) {
                console.log('ℹ️ [CompositeCardRenderer] No artifact in metadata, using fallback (sampled)', {
                  messageText: message.text?.substring(0, 50),
                  hasArtifactId: !!artifactId,
                  metadataKeysCount: Object.keys(message.metadata).length
                });
              }
              cardData = {
                artifact: {
                  id: artifactId || 'unknown',
                  name: artifactName,
                  type: 'artifact',
                  attrs: {},
                  items: []
                },
                selectedArtifactId: artifactId,
                allArtifacts: []
              };
            }
            break;
          }

          case 'task-detail':
            cardTypeEnum = 'artifact_item' as ContentCardType;
            // Extract task data from message metadata
            if ('task' in message.metadata && (message.metadata as any).task) {
              cardData = {
                artifactItem: (message.metadata as any).task,
                parentArtifact: {
                  id: 'parent',
                  name: 'Parent Process',
                  type: 'process',
                  attrs: {},
                  items: []
                }
              };
            } else {
              // Fallback - create mock task data
              cardData = {
                artifactItem: {
                  id: taskId || 'unknown',
                  name: 'Unknown Task',
                  state: 'pending'
                },
                parentArtifact: {
                  id: 'parent',
                  name: 'Parent Process',
                  type: 'process',
                  attrs: {},
                  items: []
                }
              };
            }
            break;

          case 'artifact-list': {
            cardTypeEnum = 'list' as ContentCardType;
            // Get items from metadata.items or message.cards (for artifact_list messages)
            const artifactItems = (message.metadata as any).items || 
                                 ((message as any).cards && Array.isArray((message as any).cards) ? (message as any).cards : []) ||
                                 [];
            console.log('🔍 [CompositeCardRenderer] artifact-list items:', {
              fromMetadata: (message.metadata as any).items?.length || 0,
              fromCards: ((message as any).cards && Array.isArray((message as any).cards) ? (message as any).cards.length : 0),
              totalItems: artifactItems.length
            });
            cardData = {
              items: artifactItems,
              itemType: 'artifact'
            };
            break;
          }

          case 'task-list':
            cardTypeEnum = 'list' as ContentCardType;
            cardData = {
              items: (message.metadata as any).items || [],
              itemType: 'task'
            };
            break;

          case 'state_transition':
            cardTypeEnum = 'artifact_item' as ContentCardType;
            // Extract state transition data from message metadata
            if ((message.metadata as any).artifact) {
              cardData = {
                artifactItem: {
                  id: (message.metadata as any).artifact_item_id || 'unknown',
                  name: (message.metadata as any).artifact.name,
                  state: (message.metadata as any).current_state || 'unknown',
                  availableTransitions: (message.metadata as any).available_transitions || [],
                  actions: (message.metadata as any).actions || []
                },
                parentArtifact: (message.metadata as any).artifact
              };
            } else {
              // Fallback - create mock state transition data
              cardData = {
                artifactItem: {
                  id: 'unknown',
                  name: 'Unknown Item',
                  state: 'unknown',
                  availableTransitions: [],
                  actions: []
                },
                parentArtifact: {
                  id: 'parent',
                  name: 'Parent Artifact',
                  type: 'artifact',
                  attrs: {},
                  items: []
                }
              };
            }
            break;

          case 'process-flow':
          case 'flow-diagram':  // Legacy name, treat as process-flow
            // Process flow visualization - render directly without card agent
            setLoading(false);
            return; // Handled by direct render below
            
          case 'organization-assessment':
          case 'value-dashboard':
          case 'financial-dashboard':
          case 'simulation-controls':
          case 'problem-detection':
          case 'integration-hub':
          case 'governance-dashboard':
          case 'lead-capture':
            // Demo cards - render directly for now
            console.log(`Rendering demo card: ${cardType}`);
            setLoading(false);
            return; // Handled by direct render below

          case 'prime_artifact':
            console.log('🔍 [CompositeCardRenderer] Processing prime artifact card');
            cardTypeEnum = 'prime_artifact' as ContentCardType;
            cardData = {
              primeArtifact: (message.metadata as any).primeArtifact || (message.metadata as any).artifact,
              artifactId: artifactId || (message.metadata as any).primeArtifact?.id,
            } as any;
            break;

          case 'prime_states':
            console.log('🔍 [CompositeCardRenderer] Processing prime states card');
            cardTypeEnum = 'prime_states' as ContextCardType;
            cardData = {
              primeArtifact: (message.metadata as any).primeArtifact || (message.metadata as any).artifact,
              artifactId: artifactId || (message.metadata as any).primeArtifact?.id,
            } as any;
            break;

          case 'prime_transitions':
            console.log('🔍 [CompositeCardRenderer] Processing prime transitions card');
            cardTypeEnum = 'prime_transitions' as ContextCardType;
            cardData = {
              primeArtifact: (message.metadata as any).primeArtifact || (message.metadata as any).artifact,
              artifactId: artifactId || (message.metadata as any).primeArtifact?.id,
            } as any;
            break;

          case 'prime_processes':
            console.log('🔍 [CompositeCardRenderer] Processing prime processes card');
            cardTypeEnum = 'prime_processes' as ContextCardType;
            cardData = {
              primeArtifact: (message.metadata as any).primeArtifact || (message.metadata as any).artifact,
              artifactId: artifactId || (message.metadata as any).primeArtifact?.id,
            } as any;
            break;

          case 'prime_parameters':
            console.log('🔍 [CompositeCardRenderer] Processing prime parameters card');
            cardTypeEnum = 'prime_parameters' as ContextCardType;
            cardData = {
              primeArtifact: (message.metadata as any).primeArtifact || (message.metadata as any).artifact,
              artifactId: artifactId || (message.metadata as any).primeArtifact?.id,
            } as any;
            break;

          case 'prime_skills':
            console.log('🔍 [CompositeCardRenderer] Processing prime skills card');
            cardTypeEnum = 'prime_skills' as ContextCardType;
            cardData = {
              primeArtifact: (message.metadata as any).primeArtifact || (message.metadata as any).artifact,
              artifactId: artifactId || (message.metadata as any).primeArtifact?.id,
            } as any;
            break;

          default:
            // Fallback: Try to handle Container and other variations
            if (normalizedCardType === 'container' || cardType === 'Container' || cardType === 'container') {
              console.log('🔍 [CompositeCardRenderer] Processing Container card as artifact (fallback)');
              cardTypeEnum = 'artifact' as ContentCardType;
              if ((message.metadata as any).artifact) {
                const artifact = (message.metadata as any).artifact;
                const actualArtifactId = artifact.id || artifactId;
                cardData = {
                  artifact: artifact,
                  selectedArtifactId: actualArtifactId,
                  allArtifacts: (message.metadata as any).allArtifacts || [],
                  actionConfig: (message.metadata as any).actionConfig
                };
              } else {
                console.warn('⚠️ [CompositeCardRenderer] Container card has no artifact data');
                cardData = {
                  artifact: {
                    id: artifactId || 'unknown',
                    name: message.text || 'Unknown Artifact',
                    type: 'artifact',
                    attrs: {},
                    items: []
                  },
                  selectedArtifactId: artifactId,
                  allArtifacts: []
                };
              }
              break;
            }
            
            console.warn(`Unknown card type: ${cardType}`, {
              cardType,
              normalizedCardType,
              metadataKeys: Object.keys(message.metadata),
              hasArtifact: !!(message.metadata as any).artifact
            });
            setLoading(false);
            return;
        }

        // Create the composite card
        // Only log in development mode and sample to reduce verbosity
        if (process.env.NODE_ENV === 'development' && Math.random() < 0.1) {
          console.log('🔍 [CompositeCardRenderer] Creating card with chatCardAgent (sampled):', {
            cardType: cardTypeEnum,
            hasCardData: !!cardData,
            hasActionConfig: !!(cardData as any).actionConfig,
            hasItems: !!(cardData as any)?.items,
            itemsCount: (cardData as any)?.items?.length || 0
          });
        }
        
        const compositeCard = await chatCardAgent.createCard(
          cardTypeEnum,
          cardData,
          cardContext,
          CardLocation.CONTENT
        );

        // Only log in development mode and sample to reduce verbosity
        if (process.env.NODE_ENV === 'development' && Math.random() < 0.1) {
          console.log('🔍 [CompositeCardRenderer] Card created successfully (sampled):', compositeCard.id);
        }

        // Add command history metadata if available
        if (message.metadata.commandHistory) {
          compositeCard.metadata.commandHistory = message.metadata.commandHistory;
        }

        setCard(compositeCard);
      } catch (error) {
        console.error('❌ Error creating composite card:', {
          error,
          errorMessage: error instanceof Error ? error.message : String(error),
          errorStack: error instanceof Error ? error.stack : undefined,
          messageType: message.type,
          messageMetadata: message.metadata,
          cardType: (message.metadata as any)?.cardType,
          context
        });
      } finally {
        setLoading(false);
      }
    };

    renderCardFromMessage();
  }, [message, context]);

  const handleCardSelect = (cardId: string) => {
    if (card && onCardSelect) {
      onCardSelect(card);
    }
  };

  const handleCardCollapse = (cardId: string, collapsed: boolean) => {
    if (card) {
      chatCardAgent.updateCard(cardId, { isCollapsed: collapsed }).then(updatedCard => {
        setCard(updatedCard);
      });
    }
  };

  const handleCardPin = (cardId: string, pinned: boolean) => {
    if (card) {
      if (pinned) {
        chatCardAgent.pinCard(cardId);
      } else {
        chatCardAgent.unpinCard(cardId);
      }
      chatCardAgent.updateCard(cardId, { isPinned: pinned }).then(updatedCard => {
        setCard(updatedCard);
      });
    }
  };

  const handleCardTrigger = (trigger: any) => {
    if (onCardAction) {
      onCardAction('trigger', { trigger, cardId: card?.id });
    }
  };

  const handleCardAction = (action: string, payload?: any) => {
    if (onCardAction) {
      onCardAction(action, { ...payload, cardId: card?.id });
    }
  };

  if (loading) {
    return (
      <div style={{
        padding: '16px',
        backgroundColor: '#f9fafb',
        borderRadius: '8px',
        border: '1px solid #e5e7eb'
      }}>
        <div style={{ 
          fontSize: '14px', 
          color: '#6b7280',
          textAlign: 'center'
        }}>
          Loading card...
        </div>
      </div>
    );
  }

  // Handle demo cards that render directly without card agent
  // Compute cardContext outside useEffect for use in render
  const defaultContext: CardContext = {
    threadId: context?.threadId || 'default',
    userId: context?.userId || 'current-user',
    uiRole: context?.uiRole || 'architect',
    modelType: context?.modelType || 'Company',
    dataSource: context?.dataSource || 'live'
  };
  
  const cardContext = context && context.threadId && context.userId && context.uiRole && context.modelType
    ? context
    : defaultContext;
  
  const cardType = message.metadata?.cardType;
  if ((cardType === 'process-flow' || cardType === 'flow-diagram') && !loading && message.metadata) {
    return (
      <ProcessFlowCard
        metadata={message.metadata as any}
        threadId={cardContext.threadId}
        onContextSelect={(type, id, data) => {
          console.log(`Context selected: ${type} ${id}`, data);
          // This will be wired up to show context cards
        }}
      />
    );
  }

  // Handle other demo cards (to be implemented)
  const demoCardTypes = ['organization-assessment', 'value-dashboard', 'financial-dashboard', 
                          'simulation-controls', 'problem-detection', 'integration-hub',
                          'governance-dashboard', 'lead-capture'];
  if (cardType && demoCardTypes.includes(cardType) && !loading) {
    return (
      <div style={{
        padding: '20px',
        backgroundColor: '#eff6ff',
        borderRadius: '8px',
        border: '2px solid #3b82f6'
      }}>
        <div style={{ fontSize: '16px', fontWeight: 600, color: '#1e40af', marginBottom: '8px' }}>
          {cardType.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')} Card
        </div>
        <div style={{ fontSize: '14px', color: '#3b82f6' }}>
          Card component not yet implemented. Card data available in message metadata.
        </div>
        <details style={{ marginTop: '12px' }}>
          <summary style={{ cursor: 'pointer', color: '#6b7280', fontSize: '12px' }}>
            View card metadata
          </summary>
          <pre style={{ 
            marginTop: '8px', 
            padding: '12px', 
            background: 'white', 
            borderRadius: '4px',
            fontSize: '11px',
            overflow: 'auto',
            maxHeight: '300px'
          }}>
            {JSON.stringify(message.metadata, null, 2)}
          </pre>
        </details>
      </div>
    );
  }

  if (!card) {
    // Only log in development mode
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 [CompositeCardRenderer] Rendering: no card available');
    }
    return (
      <div style={{
        padding: '16px',
        backgroundColor: '#fef2f2',
        borderRadius: '8px',
        border: '1px solid #fecaca'
      }}>
        <div style={{ 
          fontSize: '14px', 
          color: '#dc2626',
          textAlign: 'center'
        }}>
          Unable to render card
        </div>
      </div>
    );
  }
  
  // Only log in development mode and sample to reduce verbosity
  if (process.env.NODE_ENV === 'development' && Math.random() < 0.1) {
    console.log('🔍 [CompositeCardRenderer] Rendering card (sampled):', card.id);
  }
  return (
    <div style={{ margin: '8px 0' }}>
      <CompositeCardComponent
        card={card}
        onSelect={handleCardSelect}
        onCollapse={handleCardCollapse}
        onPin={handleCardPin}
        onTrigger={handleCardTrigger}
        onAction={handleCardAction}
      />
    </div>
  );
};
